import ROUTES from "../consts/routes";

/**
 * Determines the default navigation path for a user based on permissions.
 *
 * @param {Object} params - Parameters for determining the default path.
 * @param {Object} params.userData - The user's data object containing permissions and organization info.
 * @param {Function} params.isAdmin - Function to check if the user has admin privileges.
 * @param {Function} params.hasOrgPermission - Function to check if the user has permissions for specific organization types.
 * @param {string|null} params.skipPath - The path to exclude from consideration to prevent redirect loops.
 *
 * @returns {string} - The default path the user should be redirected to.
 */
export const getDefaultPath = ({
  userData,
  isAdmin,
  hasOrgPermission,
  skipPath = null,
}) => {
  if (!userData) return "/404";

  const userPermissionNames = new Set(userData.permissions.map((p) => p.name));

  for (let route of ROUTES) {
    const {
      requiredPermissions = [],
      orgTypes = [],
      roles = [],
      envAccess = true,
      superuserOnly = false,
      path,
    } = route;

    if (path === skipPath) continue;

    if (superuserOnly && !userData?.is_superuser) continue;

    const hasOrganizationPermission = orgTypes.length
      ? orgTypes.some((orgType) => hasOrgPermission(orgType))
      : true;

    // Check role permissions
    const hasRolePermission = roles.length
      ? roles.some((role) => {
          if (role === "admin") return isAdmin();
          return false;
        })
      : true;

    // Check required permissions
    let hasRequiredPermission = true;
    if (requiredPermissions.length > 0) {
      hasRequiredPermission = requiredPermissions.some((perm) =>
        userPermissionNames.has(perm),
      );
    }

    if (
      (hasRolePermission || userData?.is_superuser) &&
      hasOrganizationPermission &&
      envAccess &&
      (hasRequiredPermission || userData?.is_superuser)
    ) {
      return path;
    }
  }

  return "/404";
};
